
import React, { useState } from 'react';
import { Plus, Copy, ExternalLink, MoreHorizontal, Trash, Edit, BarChart } from 'lucide-react';
import { useReferralCodes, useCreateReferralCode } from '@/hooks/useAffiliate';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useToast } from '@/components/ui/use-toast';
import { DatePicker } from '@/components/ui/date-picker';
import { useIsMobile } from '@/hooks/use-mobile';

const formSchema = z.object({
  description: z.string().min(3, { message: "Description must be at least 3 characters" }),
  expiresAt: z.date().optional(),
});

const AffiliateLinks = () => {
  const { data: referralCodes, isLoading } = useReferralCodes();
  const { mutate: createReferralCode } = useCreateReferralCode();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createReferralCode({
      description: values.description,
      expiresAt: values.expiresAt,
    }, {
      onSuccess: (data) => {
        if (data) {
          toast({
            title: "Referral Link Created",
            description: "Your new referral link has been created successfully.",
          });
          form.reset();
          setOpen(false);
        } else {
          toast({
            title: "Error",
            description: "Could not create referral link.",
            variant: "destructive",
          });
        }
      },
    });
  };

  const copyToClipboard = (code: string) => {
    const baseUrl = window.location.origin;
    const referralUrl = `${baseUrl}/?ref=${code}`;
    navigator.clipboard.writeText(referralUrl);
    toast({
      title: "Copied to clipboard",
      description: "Referral link has been copied to clipboard.",
    });
  };

  const getFullReferralUrl = (code: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${code}`;
  };

  return (
    <div className="space-y-4 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-bold">Referral Links</h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            Create and manage your custom referral links
          </p>
        </div>
        
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size={isMobile ? "sm" : "default"}>
              <Plus className={`${isMobile ? "mr-1 h-3 w-3" : "mr-2 h-4 w-4"}`} /> Create Link
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Create New Referral Link</DialogTitle>
              <DialogDescription>
                Create a custom link to track specific marketing campaigns.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 md:space-y-6 pt-4">
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Input placeholder="Facebook Campaign" {...field} />
                      </FormControl>
                      <FormDescription className={isMobile ? "text-xs" : "text-sm"}>
                        A name to help you identify this link.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="expiresAt"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Expiration Date (Optional)</FormLabel>
                      <DatePicker
                        date={field.value} 
                        setDate={field.onChange}
                      />
                      <FormDescription className={isMobile ? "text-xs" : "text-sm"}>
                        The link will expire after this date.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
            <DialogFooter className="mt-6">
              <Button variant="outline" size={isMobile ? "sm" : "default"} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size={isMobile ? "sm" : "default"} onClick={form.handleSubmit(onSubmit)}>
                Create Link
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader className={isMobile ? "p-4" : ""}>
          <CardTitle className={isMobile ? "text-lg" : "text-2xl"}>Your Links</CardTitle>
          <CardDescription className={isMobile ? "text-xs" : "text-sm"}>All your referral links and their performance</CardDescription>
        </CardHeader>
        <CardContent className={isMobile ? "p-4 pt-0" : ""}>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <p className={isMobile ? "text-sm" : ""}>Loading links...</p>
            </div>
          ) : referralCodes && referralCodes.length > 0 ? (
            <div className="overflow-auto -mx-4 md:mx-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className={isMobile ? "text-xs" : ""}>Description</TableHead>
                    <TableHead className={`${isMobile ? "text-xs" : ""} hidden sm:table-cell`}>Code</TableHead>
                    <TableHead className={`${isMobile ? "text-xs" : ""} hidden md:table-cell`}>Created</TableHead>
                    <TableHead className={`${isMobile ? "text-xs" : ""} hidden md:table-cell`}>Expires</TableHead>
                    <TableHead className={`${isMobile ? "text-xs" : ""} hidden md:table-cell`}>Status</TableHead>
                    <TableHead className={isMobile ? "text-xs" : ""}>Performance</TableHead>
                    <TableHead className={`${isMobile ? "text-xs text-right" : "text-right"}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referralCodes.map((link) => (
                    <TableRow key={link.code}>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} font-medium`}>{link.description}</TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} font-mono text-sm hidden sm:table-cell`}>{link.code}</TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} hidden md:table-cell`}>
                        {new Date(link.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} hidden md:table-cell`}>
                        {link.expires_at ? new Date(link.expires_at).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} hidden md:table-cell`}>
                        <Badge variant={link.is_active ? "outline" : "secondary"}>
                          {link.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""}`}>
                        <span className="flex items-center space-x-1">
                          <span>{link.clicks} clicks</span>
                          <span className="text-muted-foreground">/</span>
                          <span className="text-primary">{link.conversions} conv.</span>
                        </span>
                      </TableCell>
                      <TableCell className={`${isMobile ? "text-xs py-2" : ""} text-right`}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className={isMobile ? "h-6 w-6 p-0" : "h-8 w-8 p-0"}>
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => copyToClipboard(link.code)}>
                              <Copy className="mr-2 h-4 w-4" /> Copy Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => window.open(getFullReferralUrl(link.code), '_blank')}>
                              <ExternalLink className="mr-2 h-4 w-4" /> Open Link
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled>
                              <BarChart className="mr-2 h-4 w-4" /> View Stats
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled>
                              <Edit className="mr-2 h-4 w-4" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem disabled className="text-destructive">
                              <Trash className="mr-2 h-4 w-4" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-6 md:py-10">
              <p className={`${isMobile ? "text-xs" : "text-sm"} text-muted-foreground`}>No referral links found. Create your first link to get started.</p>
              <Button className="mt-4" size={isMobile ? "sm" : "default"} onClick={() => setOpen(true)}>
                <Plus className={`${isMobile ? "mr-1 h-3 w-3" : "mr-2 h-4 w-4"}`} /> Create Link
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateLinks;
